// (9 Oct 2026, 8.19/8.20, generalised the same day: decisions 64-67) The calendar history.
// Replaces utils/culinary.js. The same reading is in the BackEnd (calendarHistory.js):
// keep the two in step.
//
// A calendar's settings: history_enabled, history_keyword (e.g. "Culinary provenance:"),
// history_index_label (e.g. "Country"), history_show_menu, history_curator_email.
//   - With a keyword, an event is in the history when its Notes' first non-blank line
//     begins with the keyword; the rest of that line is its keyword value ("Sumatra").
//   - Without a keyword, every event is in the history.

const clean = (s) => (typeof s === 'string' ? s.trim() : '');

// The keyword's value from an event's Notes, or null when the Notes do not begin with it.
export const keywordValueOf = (notes, keyword) => {
  const kw = clean(keyword);
  if (!kw) return null;
  const firstLine = String(notes || '').split(/\r?\n/).map((l) => l.trim()).find((l) => l !== '') || '';
  if (firstLine.slice(0, kw.length).toLowerCase() !== kw.toLowerCase()) return null;
  const value = firstLine.slice(kw.length).replace(/^[:\s]+/, '').replace(/[.;,]+$/, '').trim();
  return value || null;
};

// Is this event in the calendar's history? -> { inHistory, keywordValue }
export const historyReadingOf = (calendar, notes) => {
  if (!calendar || calendar.history_enabled !== true) return { inHistory: false, keywordValue: null };
  if (!clean(calendar.history_keyword)) return { inHistory: true, keywordValue: null };
  const keywordValue = keywordValueOf(notes, calendar.history_keyword);
  return { inHistory: !!keywordValue, keywordValue };
};

// "Culinary provenance:" -> "Culinary provenance" (headings and messages).
export const keywordLabelOf = (keyword) => clean(keyword).replace(/[:\s]+$/, '');

// The index column's name when the calendar asks for one, else null.
export const indexLabelOf = (calendar) =>
  (calendar && calendar.history_enabled === true && clean(calendar.history_index_label)) || null;

// The event needs its index value (Hot Club: the Country). The message to show, or null.
export const missingIndexMessage = (calendar, notes, indexValue) => {
  const label = indexLabelOf(calendar);
  if (!label) return null;
  const reading = historyReadingOf(calendar, notes);
  if (!reading.inHistory || clean(indexValue)) return null;
  return reading.keywordValue
    ? `Please give the ${label} for ${reading.keywordValue}.`
    : `Please give the ${label} for this event.`;
};

// (decision 66) Add Event's Notes start with the keyword and one space. Notes that
// already begin with it are left alone; otherwise the keyword goes first, with any
// note already there (a schedule month's own note) on the line below.
export const notesWithKeyword = (calendar, notes) => {
  if (!calendar || calendar.history_enabled !== true) return notes || '';
  const kw = clean(calendar.history_keyword);
  if (!kw) return notes || '';
  const current = String(notes || '');
  if (current.trim().toLowerCase().startsWith(kw.toLowerCase())) return current;
  return current.trim() ? `${kw} \n${current}` : `${kw} `;
};

// Notes left with the keyword alone on the first line (nothing typed after it) lose that
// line when saved, so an event the GA did not mean for the history carries no stray keyword.
export const withoutBareKeyword = (calendar, notes) => {
  const kw = clean(calendar && calendar.history_keyword);
  const current = String(notes || '');
  if (!kw || !calendar || calendar.history_enabled !== true) return current;
  const lines = current.split(/\r?\n/);
  const first = lines.findIndex((l) => l.trim() !== '');
  if (first < 0) return current;
  const bare = lines[first].trim().replace(/[:\s]+$/, '').toLowerCase() === kw.replace(/[:\s]+$/, '').toLowerCase();
  if (!bare) return current;
  return lines.slice(first + 1).join('\n').trim();
};
